/**
 * <klapi-panier> : tiroir panier Klapi.
 *
 * - S'ouvre avec l'événement `klapi:panier` ({ ouvrir: true }), émis après un ajout au panier,
 *   ou au clic sur [data-panier-ouvrir] (icône du header).
 * - Son contenu est rafraîchi par la Section Rendering API (section "klapi-panier"), et les
 *   compteurs [data-panier-compte] du header suivent cart.item_count sans recharger la page.
 * - "Retirer" ([data-panier-retirer]) passe la ligne à 0 ; la montée en gamme
 *   ([data-panier-montee]) remplace la formule par la variante proposée.
 * - Fermeture : ✕, Échap, clic sur le fond. Focus piégé, scroll bloqué, focus rendu au déclencheur.
 */
if (!customElements.get('klapi-panier')) {
  class KlapiPanier extends HTMLElement {
    connectedCallback() {
      this.racine = window.Shopify?.routes?.root || '/';
      this.declencheur = null;

      this.addEventListener('click', (event) => this.surClic(event));
      this.surClavier = this.surClavier.bind(this);
      document.addEventListener('keydown', this.surClavier);

      document.addEventListener('klapi:panier', async (event) => {
        if (event.detail?.declencheur) this.declencheur = event.detail.declencheur;
        await this.rafraichir();
        if (event.detail?.ouvrir) this.ouvrir();
      });

      document.addEventListener('click', (event) => {
        const bouton = event.target.closest('[data-panier-ouvrir]');
        if (!bouton) return;
        event.preventDefault();
        this.declencheur = bouton;
        this.rafraichir().then(() => this.ouvrir());
      });

      // /cart et ?panier=ouvert ouvrent directement le tiroir
      if (new URLSearchParams(window.location.search).has('panier') || /\/cart\/?$/.test(window.location.pathname)) {
        this.ouvrir();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.surClavier);
    }

    ouvrir() {
      this.declencheur = this.declencheur || document.activeElement;
      this.hidden = false;
      document.documentElement.setAttribute('scroll-lock', '');
      requestAnimationFrame(() => this.querySelector('.k-panier__tiroir')?.focus());
    }

    fermer() {
      this.hidden = true;
      document.documentElement.removeAttribute('scroll-lock');
      this.declencheur?.focus?.();
      this.declencheur = null;
    }

    async rafraichir() {
      const reponse = await fetch(`${this.racine}?sections=klapi-panier`);
      if (!reponse.ok) return;
      const html = (await reponse.json())['klapi-panier'];
      const nouveau = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-panier-contenu]');
      const actuel = this.querySelector('[data-panier-contenu]');
      if (nouveau && actuel) actuel.replaceWith(nouveau);
      this.majCompteurs(Number(nouveau?.dataset.compte || 0));
    }

    majCompteurs(nombre) {
      document.querySelectorAll('[data-panier-compte]').forEach((pastille) => {
        pastille.textContent = nombre;
        pastille.hidden = nombre === 0;
      });
      document.querySelectorAll('[data-panier-ouvrir]').forEach((bouton) => {
        bouton.setAttribute('aria-label', `Panier, ${nombre} article${nombre > 1 ? 's' : ''}`);
        bouton.toggleAttribute('data-plein', nombre > 0);
      });
    }

    async modifier(cle, quantite) {
      await fetch(`${this.racine}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: cle, quantity: quantite }),
      });
    }

    async surClic(event) {
      if (event.target.closest('[data-panier-fermer]')) {
        this.fermer();
        return;
      }

      const retirer = event.target.closest('[data-panier-retirer]');
      if (retirer) {
        retirer.setAttribute('aria-busy', 'true');
        await this.modifier(retirer.dataset.panierRetirer, 0);
        await this.rafraichir();
        this.querySelector('.k-panier__tiroir')?.focus();
        return;
      }

      const montee = event.target.closest('[data-panier-montee]');
      if (montee) {
        montee.setAttribute('aria-busy', 'true');
        await this.modifier(montee.dataset.panierLigne, 0);
        const plan = montee.dataset.plan;
        await fetch(`${this.racine}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(montee.dataset.panierMontee), quantity: 1, ...(plan ? { selling_plan: Number(plan) } : {}) }],
          }),
        });
        await this.rafraichir();
        this.querySelector('.k-panier__tiroir')?.focus();
      }
    }

    surClavier(event) {
      if (this.hidden) return;
      if (event.key === 'Escape') {
        this.fermer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = [...this.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
        (el) => el.offsetParent !== null
      );
      if (!focusables.length) return;
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === premier || !this.contains(document.activeElement))) {
        event.preventDefault();
        dernier.focus();
      } else if (!event.shiftKey && document.activeElement === dernier) {
        event.preventDefault();
        premier.focus();
      }
    }
  }

  customElements.define('klapi-panier', KlapiPanier);
}
